import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
    Area,
    AreaChart,
    CartesianGrid,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import { ALERT_TYPE, SEVERITY } from "./ownerDashboardData";

// Presentational pieces of the Owner Dashboard. No data fetching or business
// rules here — values arrive already computed by ownerDashboardData.js.

const fmtDateTime = (ms) =>
    new Date(ms).toLocaleString(undefined, {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
    });

export const fmtPcs = (n) => `${Number(n || 0).toLocaleString()} pcs`;
export const fmtHours = (seconds) => `${(seconds / 3600).toFixed(1)} h`;

export function CardSpinner() {
    return (
        <div className="od-spinner">
            <i className="fa fa-spinner fa-spin" style={{ fontSize: "1.6rem", color: "#6c757d" }} />
        </div>
    );
}

// ▲/▼ with a colour that says whether the move is good or bad. tone="neutral"
// for rows where up/down is not a judgement (e.g. target).
export function ChangeCell({ change, tone }) {
    if (!change) return <span className="od-na">—</span>;
    const arrow = change.direction === "up" ? "▲" : change.direction === "down" ? "▼" : "■";
    const cls =
        tone === "neutral" || change.direction === "flat"
            ? "od-neutral"
            : change.good
              ? "od-good"
              : "od-bad";
    return (
        <span className={cls}>
            {arrow} {Math.abs(change.pct).toFixed(1)}%
        </span>
    );
}

// AdminLTE small-box colours with the same light NW→SE gradient as the main
// Dashboard's Tile, plus purple for alerts.
const BOX_GRADIENTS = {
    primary: "linear-gradient(135deg, #3d88ff 0%, #007bff 100%)",
    success: "linear-gradient(135deg, #3eb058 0%, #28a745 100%)",
    warning: "linear-gradient(135deg, #ffb340 0%, #fd9a00 100%)",
    danger: "linear-gradient(135deg, #e04958 0%, #dc3545 100%)",
    purple: "linear-gradient(135deg, #8357d1 0%, #6f42c1 100%)",
    info: "linear-gradient(135deg, #2eabbf 0%, #17a2b8 100%)",
};

// A coloured KPI box: big value, label, one supporting line, watermark icon and
// a "View details" footer. `extra` replaces the supporting line (alert counts).
export function KpiCard({
    icon,
    theme = "primary",
    label,
    value,
    sub,
    extra,
    to,
    linkText = "View Details",
    footerIcon = "fas fa-arrow-circle-right",
    muted,
}) {
    const footer = (
        <>
            {linkText} <i className={`${footerIcon} ml-1`} />
        </>
    );
    return (
        <div className="col-xl-3 col-md-6">
            <div
                className={`small-box od-box ${muted ? "od-box-muted" : ""}`}
                style={{ background: BOX_GRADIENTS[theme] || BOX_GRADIENTS.primary }}
            >
                <div className="inner">
                    <div className="od-box-value">{value}</div>
                    <div className="od-box-label">{label}</div>
                    <div className="od-box-sub">{extra || sub || "\u00a0"}</div>
                </div>
                <div className="icon">
                    <i className={icon} />
                </div>
                {to ? (
                    <Link to={to} className="small-box-footer">
                        {footer}
                    </Link>
                ) : (
                    <span className="small-box-footer od-box-footer-static">{muted ? "Coming soon" : footer}</span>
                )}
            </div>
        </div>
    );
}

// White AdminLTE card with the title on the left and a collapse button on the
// right, the way every panel on the screenshot-approved layout reads.
export function DashCard({ icon, title, tools, footer, loading, bodyClassName = "", children }) {
    const [collapsed, setCollapsed] = useState(false);
    return (
        <div className={`card od-card ${collapsed ? "collapsed-card" : ""}`}>
            {loading && !collapsed && <CardSpinner />}
            <div className="card-header">
                <h3 className="card-title">
                    {icon && <i className={`${icon} mr-2 od-card-icon`} />}
                    {title}
                </h3>
                <div className="card-tools">
                    {tools}
                    <button
                        type="button"
                        className="btn btn-tool"
                        onClick={() => setCollapsed((c) => !c)}
                        aria-label={collapsed ? "Expand" : "Collapse"}
                    >
                        <i className={`fas ${collapsed ? "fa-plus" : "fa-minus"}`} />
                    </button>
                </div>
            </div>
            {!collapsed && (
                <>
                    <div className={`card-body ${bodyClassName}`}>{children}</div>
                    {footer && <div className="card-footer od-card-footer">{footer}</div>}
                </>
            )}
        </div>
    );
}

const FooterLink = ({ to, children }) => (
    <Link to={to} className="od-footer-link">
        {children} <i className="fas fa-arrow-right ml-1" />
    </Link>
);

export function PerformanceTable({ rows, loading }) {
    return (
        <DashCard
            icon="fas fa-chart-area"
            title="Performance Overview (Today)"
            loading={loading}
            bodyClassName="p-0 table-responsive"
        >
            <table className="table od-table">
                <thead>
                    <tr>
                        <th>KPI</th>
                        <th>Today (so far)</th>
                        <th>Yesterday (same time)</th>
                        <th>Change</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row) => (
                        <tr key={row.label}>
                            <td>{row.label}</td>
                            <td className={row.na ? "od-na" : "od-strong"}>{row.today}</td>
                            <td className={row.na ? "od-na" : "od-strong"}>{row.yesterday}</td>
                            <td>
                                {row.na ? (
                                    <span className="od-na">{row.today}</span>
                                ) : (
                                    <ChangeCell change={row.change} tone={row.tone} />
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </DashCard>
    );
}

const ALERT_ICONS = {
    [ALERT_TYPE.LONG_DOWNTIME]: "fas fa-exclamation-triangle",
    [ALERT_TYPE.BEHIND_TARGET]: "fas fa-bullseye",
    [ALERT_TYPE.UNDER_MAINTENANCE]: "fas fa-tools",
};

// "Job A behind target — 30 of 50 expected so far" reads as a title plus a detail line.
const splitAlertText = (text) => {
    const i = text.indexOf(" — ");
    return i === -1 ? [text, null] : [text.slice(0, i), text.slice(i + 3)];
};

// partial: some alert sources could not be loaded, so "no alerts" is not "all good".
export function AlertsCard({ alerts, loading, partial }) {
    const highCount = alerts.filter((a) => a.severity === SEVERITY.HIGH).length;
    return (
        <DashCard
            title="Top Alerts"
            loading={loading}
            bodyClassName="p-0 od-alerts-body"
            tools={
                highCount > 0 && (
                    <span className="od-high-pill">
                        <span className="od-high-count">{highCount}</span> High Priority
                    </span>
                )
            }
        >
            {partial && !loading && (
                <div className="od-alert od-na">
                    <i className="fa fa-exclamation-triangle mr-1" />
                    Production / downtime alerts unavailable — data could not be loaded.
                </div>
            )}
            {alerts.length === 0 && !partial && (
                <div className="od-empty">
                    <i className="fa fa-check-circle fa-2x mb-2 text-success" />
                    No business alerts
                </div>
            )}
            {alerts.map((a) => {
                const high = a.severity === SEVERITY.HIGH;
                const [title, detail] = splitAlertText(a.text);
                return (
                    <div key={a.key} className="od-alert">
                        <i
                            className={`${ALERT_ICONS[a.type] || "fas fa-exclamation-circle"} od-alert-icon ${high ? "od-bad" : "od-warn"}`}
                        />
                        <div className="od-alert-text">
                            <div className="od-alert-title">{title}</div>
                            {detail && <div className="od-alert-time">{detail}</div>}
                            {/* Behind-target is stamped with "now", so a time adds nothing there. */}
                            {Number.isFinite(a.time) && a.type !== ALERT_TYPE.BEHIND_TARGET && (
                                <div className="od-alert-time">Since {fmtDateTime(a.time)}</div>
                            )}
                        </div>
                        <span className={`od-sev ${high ? "od-sev-high" : "od-sev-medium"}`}>{a.severity}</span>
                    </div>
                );
            })}
        </DashCard>
    );
}

export function TrendCard({ trend, loading, unavailable }) {
    const hasAny = trend.some((d) => d.count > 0);
    return (
        <DashCard icon="fas fa-chart-line" title="Production Trend (Last 7 Days)" loading={loading}>
            {unavailable && !loading ? (
                <div className="od-empty">Production data unavailable.</div>
            ) : !hasAny && !loading ? (
                <div className="od-empty">No production counted against jobs in the last 7 days.</div>
            ) : (
                <div className="od-chart">
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={trend} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                            <defs>
                                <linearGradient id="odTrendFill" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor="#007bff" stopOpacity={0.18} />
                                    <stop offset="100%" stopColor="#007bff" stopOpacity={0} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" />
                            <XAxis dataKey="label" tick={{ fontSize: 12, fill: "#6c757d" }} axisLine={false} tickLine={false} />
                            <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "#6c757d" }} width={48} axisLine={false} tickLine={false} />
                            <Tooltip formatter={(v) => [fmtPcs(v), "Production"]} />
                            <Area
                                type="linear"
                                dataKey="count"
                                stroke="#007bff"
                                strokeWidth={2}
                                fill="url(#odTrendFill)"
                                dot={{ r: 4, fill: "#007bff", strokeWidth: 0 }}
                                activeDot={{ r: 5 }}
                                isAnimationActive={false}
                            />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
            )}
        </DashCard>
    );
}

const scheduleFrom = (s) => {
    let attrs = s.schedule_attributes;
    if (typeof attrs === "string") {
        try {
            attrs = JSON.parse(attrs);
        } catch {
            attrs = {};
        }
    }
    return attrs?.from ? new Date(attrs.from).getTime() : null;
};

function MaintList({ items }) {
    if (!items.length) return null;
    return (
        <ul className="od-maint-list">
            {items.slice(0, 3).map((s) => (
                <li key={s.id}>
                    {s.device?.name || s.device_id} — {s.name}
                    {scheduleFrom(s) != null && ` (${fmtDateTime(scheduleFrom(s))})`}
                </li>
            ))}
            {items.length > 3 && <li>+{items.length - 3} more</li>}
        </ul>
    );
}

function MaintStat({ icon, tone, value, label, note }) {
    return (
        <div className="od-maint-stat">
            <i className={`${icon} od-maint-icon ${tone}`} />
            <div className={`od-maint-value ${value == null ? "od-na" : ""}`}>{value ?? "—"}</div>
            <div className="od-maint-label">{label}</div>
            {note && <div className="od-maint-note">{note}</div>}
        </div>
    );
}

export function MaintenanceCard({ upcoming, underMaintenance, loading }) {
    return (
        <DashCard
            title="Maintenance Summary"
            loading={loading}
            footer={<FooterLink to="/maintenance">View maintenance schedules</FooterLink>}
        >
            <div className="od-maint-stats">
                <MaintStat icon="far fa-clock" tone="od-warn" value={upcoming.length} label="Upcoming" />
                <MaintStat icon="fas fa-tools" tone="od-info" value={underMaintenance.length} label="In Progress" />
                <MaintStat icon="far fa-calendar-check" tone="od-good" value={null} label="Completed" note="Phase 2" />
                <MaintStat icon="fas fa-exclamation-triangle" tone="od-bad" value={null} label="Overdue" note="Phase 2" />
            </div>
            {(upcoming.length > 0 || underMaintenance.length > 0) && (
                <div className="od-maint-lists">
                    {underMaintenance.length > 0 && (
                        <>
                            <div className="od-maint-head">In progress</div>
                            <MaintList items={underMaintenance} />
                        </>
                    )}
                    {upcoming.length > 0 && (
                        <>
                            <div className="od-maint-head">Next up</div>
                            <MaintList items={upcoming} />
                        </>
                    )}
                </div>
            )}
        </DashCard>
    );
}
